import { GuiButton } from '@nolimitcity/slot-launcher/bin/gui/buttons/GuiButton';
import { LabelButton } from '@nolimitcity/slot-launcher/bin/gui/buttons/LabelButton';
import { NolimitApplication } from '@nolimitcity/slot-launcher/bin/NolimitApplication';
import { Align, Direction, GuiLayout } from '@nolimitcity/slot-launcher/bin/gui/utils/GuiLayout';
import { PointerStateColorSet } from '@nolimitcity/slot-launcher/bin/gui/buttons/states/sets/PointerStateColorSet';
import { OpenSans } from '@nolimitcity/slot-launcher/bin/loader/font/OpenSans';
import { FontStyle, FontWeight } from '@nolimitcity/slot-launcher/bin/loader/font/FontStatics';
import { SlotKeypad } from '@nolimitcity/slot-keypad/bin/SlotKeypad';
import { SkinLoader } from '@nolimitcity/slot-keypad/bin/SkinLoader';
import { SlotKeypadViewSettings } from '@nolimitcity/slot-keypad/bin/core/SlotKeypadViewSettings';
import { Beheaded } from '../Beheaded';

export interface PopUpResponseData {
    confirmed: boolean;
    dontShowNextTime: boolean;
}

export class BeheadedConfirmPopupView extends PIXI.Container {
    private _background: PIXI.Sprite;
    private _popUpPlate: PIXI.NineSlicePlane;
    private _okButton: LabelButton;
    private _contentContainer: PIXI.Container;
    private _header: PIXI.Text;
    private _details: PIXI.Text;
    private _cancelButton: LabelButton;
    private _buttonClickCallback: (name: string) => void;

    static GAME_FEATURE_OK_TEXT: PIXI.TextStyle = new PIXI.TextStyle({
        fill: '#000000',
        fontFamily: OpenSans.FAMILY,
        fontSize: 38,
        fontStyle: FontStyle.ITALIC,
        fontWeight: FontWeight.EXTRA_BOLD,
        padding: 59,
    });

    private mainContainer: PIXI.Container;
    private mainWidth: number;

    get isOpen(): boolean {
        return this.visible;
    }

    constructor() {
        super();
        this.init();
    }

    public resize() {
        this.position.set(NolimitApplication.screenBounds.left, NolimitApplication.screenBounds.top);
        this._background.width = NolimitApplication.screenBounds.width;
        this._background.height = NolimitApplication.screenBounds.height;

        const popupY = NolimitApplication.isLandscape ? 0 : NolimitApplication.screenBounds.height - this._popUpPlate.height;
        this._popUpPlate.position.set(NolimitApplication.screenBounds.width * 0.5, popupY * 0.5);
    }

    private buttonClicked(btn: GuiButton) {
        SlotKeypad.playButtonSound(btn.name);
        if (this._buttonClickCallback) {
            this._buttonClickCallback(btn.name);
        }
    }

    public open(selection: number): Promise<PopUpResponseData> {
        return new Promise<PopUpResponseData>((resolve, reject) => {
            this._details.text =
                selection === 1
                    ? Beheaded.apiPlugin.translations.translate('Selectio 1 Text')
                    : Beheaded.apiPlugin.translations.translate('Selection 2 Text');

            this.show();

            this._buttonClickCallback = (name: string) => {
                const data: PopUpResponseData = {
                    confirmed: false,
                    dontShowNextTime: false,
                };
                if (name == 'ok') {
                    data.confirmed = true;
                }
                this.close();
                resolve(data);
            };
        });
    }

    public close() {
        this.hide();
    }

    private init() {
        this._background = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._background.tint = 0;
        this._background.alpha = 0.6;
        this._background.interactive = true;
        this._background.name = '_background';

        this._popUpPlate = new PIXI.NineSlicePlane(SkinLoader.getTexture(SkinLoader.CONFIRM_POP_UP), 64, 64, 64, 64);
        this._popUpPlate.width = 640;
        this._popUpPlate.height = 700;
        this._popUpPlate.pivot.set(this._popUpPlate.width * 0.5, 0);
        this._popUpPlate.name = '_popUpPlate';

        this.mainContainer = new PIXI.Container();
        this.mainContainer.position.set(50, 30);
        this.mainWidth = 540;

        const buttons = this.createButtons();
        buttons.position.set(0, 490);

        this._contentContainer = new PIXI.Container();
        this._contentContainer.name = 'content';
        this._contentContainer.position.set(this.mainWidth * 0.5, 50);

        this._header = new PIXI.Text('Warning', BeheadedConfirmPopupView.GAME_FEATURE_OK_TEXT);
        this._header.anchor.set(0.5, 0.5);

        this._details = new PIXI.Text('Are you sure?');
        this._details.anchor.set(0.5, 0.5);
        this._details.position.y = 100;

        this._contentContainer.addChild(this._header, this._details);

        this.mainContainer.addChild(buttons, this._contentContainer);
        this._popUpPlate.addChild(this.mainContainer);
        this.addChild(this._background, this._popUpPlate);

        this.close();
    }

    private createButtons(): PIXI.Container {
        const buttons = new PIXI.Container();
        let normalNonEmphasisColors = SlotKeypadViewSettings.instance.normalNonEmphasisPointerStateColors.clone();
        let activeColors = new PointerStateColorSet(0xff000000, 0xff000000, 0xff000000, 0x33000000);
        let style = BeheadedConfirmPopupView.GAME_FEATURE_OK_TEXT;

        this._cancelButton = new LabelButton(
            'cancel',
            SlotKeypad.apiPlugIn.translations.translate('CANCEL'),
            style,
            activeColors,
            normalNonEmphasisColors
        );
        this._cancelButton.resizeButtonToLabelWithMargin(10, 10, 10, 10);
        this._cancelButton.enable(true);
        this._cancelButton.addClickCallback(() => {
            this.buttonClicked(<GuiButton>this._cancelButton);
        });

        this._okButton = new LabelButton(
            'ok',
            SlotKeypad.apiPlugIn.translations.translate('OK'),
            style,
            activeColors,
            normalNonEmphasisColors
        );
        this._okButton.resizeButtonToLabelWithMargin(10, 10, 10, 10);
        this._okButton.enable(true);
        this._okButton.addClickCallback(() => {
            this.buttonClicked(<GuiButton>this._okButton);
        });

        const maxSize = Math.max(this._cancelButton.width, this._okButton.width);
        this._cancelButton.setSize(maxSize, this._cancelButton.height);
        this._okButton.setSize(maxSize, this._okButton.height);

        this._okButton.pivot.set(0, 0);
        this._cancelButton.pivot.set(0, 0);

        const okCancleBtns = new PIXI.Container();
        okCancleBtns.addChild(this._cancelButton, this._okButton);
        GuiLayout.align([this._cancelButton, this._okButton], 30, Align.LEFT, Direction.HORIZONTAL);
        okCancleBtns.position.set((this.mainWidth - okCancleBtns.width) * 0.5, 50);

        buttons.addChild(okCancleBtns);

        return buttons;
    }

    private show() {
        this.resize();
        this.visible = true;
        this._background.alpha = 0.6;
    }

    private hide() {
        this.visible = false;
    }
}
